import { useState, useEffect, useRef } from 'react';
import { ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { usePage } from '@inertiajs/react';

export default function SettlementAutocomplete({ id, value, onChange, onMunicipalityChange, error, required = false, className = '' }) {
    const { locale } = usePage().props;
    const [settlementsData, setSettlementsData] = useState([]);
    const [settlements, setSettlements] = useState([]);
    const [filteredSettlements, setFilteredSettlements] = useState([]);
    const [inputValue, setInputValue] = useState(value || '');
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const wrapperRef = useRef(null);

    // Fetch settlements from backend API proxy
    useEffect(() => {
        fetch('/api/ekatte/territorial-units')
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to fetch settlements');
                }
                return response.json();
            })
            .then(data => {
                if (Array.isArray(data) && data.length > 0) {
                    // Store full data with municipality info
                    setSettlementsData(data);
                    
                    // Extract settlement names - try different possible field names
                    const settlementNames = data.map(item => {
                        return item.name || item.Name || item.settlement || item.Settlement || item.ekatte_name || '';
                    }).filter(name => name);
                    
                    // Remove duplicates using Set and sort
                    const uniqueSettlements = [...new Set(settlementNames)].sort();
                    
                    setSettlements(uniqueSettlements);
                    setFilteredSettlements(uniqueSettlements);
                }
                setIsLoading(false);
            })
            .catch(error => {
                console.error('Error fetching settlements:', error);
                setIsLoading(false);
            });
    }, []);

    // Update input value when prop changes
    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    // Handle input change and filter settlements
    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        
        if (newValue.trim() === '') {
            setFilteredSettlements(settlements);
        } else {
            const filtered = settlements.filter(settlement =>
                settlement.toLowerCase().includes(newValue.toLowerCase())
            );
            setFilteredSettlements(filtered);
        }
        
        setIsOpen(true);
        setSelectedIndex(-1);
        onChange(newValue);
    };

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (!isOpen) return;

        const maxIndex = Math.min(filteredSettlements.length - 1, 99); // Max 100 items shown

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => (prev < maxIndex ? prev + 1 : prev));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < filteredSettlements.length) {
                    handleSelect(filteredSettlements[selectedIndex]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setSelectedIndex(-1);
                break;
        }
    };

    // Handle settlement selection
    const handleSelect = (settlement) => {
        setInputValue(settlement);
        onChange(settlement);
        setIsOpen(false);
        
        // Auto-fill municipality if callback provided
        if (onMunicipalityChange && settlementsData.length > 0) {
            const settlementData = settlementsData.find(item => {
                const itemName = item.name || item.Name || item.settlement || item.Settlement || item.ekatte_name || '';
                return itemName === settlement;
            });
            
            if (settlementData) {
                // Try different possible field names for municipality NAME (not code)
                const municipality = settlementData.municipalityName || 
                                   settlementData.municipality_name ||
                                   settlementData.MunicipalityName ||
                                   settlementData.obshtina_name ||
                                   settlementData.obstinaName ||
                                   settlementData.municipality || 
                                   settlementData.Municipality || 
                                   settlementData.obshtina || 
                                   settlementData.Obshtina ||
                                   '';
                
                if (municipality) {
                    console.log('Auto-filling municipality:', municipality, 'for settlement:', settlement);
                    onMunicipalityChange(municipality);
                } else {
                    console.log('No municipality found for settlement:', settlement, 'Settlement data:', settlementData);
                }
            } else {
                console.log('Settlement not found in data:', settlement);
            }
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const placeholder = locale === 'bg' 
        ? (isLoading ? 'Зареждане...' : 'Изберете населено място...') 
        : (isLoading ? 'Loading...' : 'Select settlement...');

    const noResultsText = locale === 'bg' 
        ? 'Няма намерени населени места' 
        : 'No settlements found';

    return (
        <div ref={wrapperRef} className={`relative ${className}`}>
            <div className="relative">
                <input
                    id={id}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setIsOpen(true)}
                    className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                        error ? 'border-red-300' : ''
                    }`}
                    placeholder={placeholder}
                    disabled={isLoading}
                    required={required}
                    autoComplete="off"
                />
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
            </div>

            {isOpen && !isLoading && filteredSettlements.length > 0 && (
                <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                    {filteredSettlements.slice(0, 100).map((settlement, index) => (
                        <div
                            key={index}
                            onClick={() => handleSelect(settlement)}
                            className={`relative cursor-pointer select-none py-2 px-3 ${
                                index === selectedIndex 
                                    ? 'bg-indigo-600 text-white' 
                                    : 'hover:bg-indigo-600 hover:text-white'
                            }`}
                        >
                            {settlement}
                        </div>
                    ))}
                    {filteredSettlements.length > 100 && (
                        <div className="py-2 px-3 text-xs text-gray-500 italic">
                            {locale === 'bg' 
                                ? `Показани първите 100 от ${filteredSettlements.length} резултата. Продължете да пишете за по-точно търсене.`
                                : `Showing first 100 of ${filteredSettlements.length} results. Continue typing for more precise search.`
                            }
                        </div>
                    )}
                </div>
            )}

            {isOpen && !isLoading && filteredSettlements.length === 0 && inputValue && (
                <div className="absolute z-10 mt-1 w-full rounded-md bg-white py-2 px-3 text-base shadow-lg ring-1 ring-black ring-opacity-5 sm:text-sm">
                    <p className="text-gray-500">{noResultsText}</p>
                </div>
            )}

            {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
            )}
        </div>
    );
}
